import React, {ChangeEvent, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {countrySpecificDistanceUnit} from '@utils/misc/functions';
import VehicleDetails from '@components/panels/VehicleDetails';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import SubmitBtn from '@components/buttons/FormButtons/SubmitBtn';
import Label from '@components/inputs/text/Label';
import NumericInput from '@components/inputs/NumericInput';
import {textBoxStyle} from '@utils/styles/textboxStyle';
import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";
import {NavBar} from '@components/navigation/NavBar/NavBar';
import DropDownMenu from '@components/inputs/DropDownMenu';
import useServiceHook from '@hooks/useServiceHook';
import {VehicleService} from '@services/Vehicles/Vehicles';
import {IFleetioStatus} from '@views/Cancellations/CancellationVehicleDetails';
import {CurrentVehicleAction, CurrentVehicleState} from './currentVehicleDetailsReducer';
import {SelectedSubscriptionState} from "@views/VehicleExchange/selectedSubscriptionReducer";


interface ICurrentVehicleProps {
  navigateTarget: string;
  selectedSubscriptionState: SelectedSubscriptionState;
  currentVehicleState: CurrentVehicleState;
  currentVehicleDispatcher: React.Dispatch<CurrentVehicleAction>;
}

export default function CurrentVehicle({
  navigateTarget,
  selectedSubscriptionState,
  currentVehicleState,
  currentVehicleDispatcher,
}: ICurrentVehicleProps) {
  const {custId, country} = useParams() as { custId: string; country: string };
  const navigate = useNavigate();


  // We get the vehicle ID from selected Subscription. Prefer the temp vehicle ID if present
  const currVehicleID = selectedSubscriptionState.temporaryVehicleId ? selectedSubscriptionState.temporaryVehicleId : selectedSubscriptionState.permanentVehicleId;
  const [loading, error, errMessage, currentVehicleDetails] = useServiceHook(
      VehicleService.getVehicleById,
      [currVehicleID, country],
      true,
      [currVehicleID],
  );

  useEffect(() => {
    // When we get a vehicle, update the state via dispatcher
    if (currentVehicleDetails) {
      currentVehicleDispatcher({
        type: 'SET_VEHICLE',
        payload: currentVehicleDetails,
      });
    }
  }, [currentVehicleDetails]);
  const vehicle = currentVehicleState?.vehicle;
  console.log('Current vehicle details:', vehicle);

  const onEndingOdometerChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    currentVehicleDispatcher({
      type: 'SET_ENDING_ODOMETER',  // Dispatch action to update the endingOdometer
      payload: value,
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(navigateTarget);
  };



  const [statusLoading, , , statuses] = useServiceHook(
    VehicleService.getVehicleStatuses,
    ['exchange', country]
  );

  const onSelectStatus = (event: ChangeEvent<HTMLSelectElement>) => {
    const statusId = event.currentTarget.value;
    const statusName = event.target.options[event.target.selectedIndex].label;

    currentVehicleDispatcher({
      type: 'SET_FLEETIO_STATUS',
      payload: { id: statusId, name: statusName },
    });
  };

  useEffect(() => {
    // Set fleetio status of curr vehicle, when status dropdown changes
     if (!statusLoading && statuses && statuses.length > 0 && currentVehicleState.fleetioStatusName == "") {
      currentVehicleDispatcher({
        type: 'SET_FLEETIO_STATUS',
        payload: {
          id: statuses[0].id,
          name: statuses[0].name,
        },
      });
    }
  }, [statusLoading, statuses]);

  useEffect(() => {
    // Set ending odometer to current meter value of vehicle when we get the vehicle details
      currentVehicleDispatcher({
        type: 'SET_ENDING_ODOMETER',
        payload: vehicle.current_meter_value,
      });
  }, [vehicle]);

    // Submit button is disabled if vehicle ID is not present
    const isSubmitDisabled = !(vehicle?.id && vehicle.id.trim().length > 0);

  return (
    <>
      <NavBar pageTitle="Current vehicle details" backAction={undefined} />
      <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={handleFormSubmit}>
        <p>Please confirm that vehicle details are correct</p>

        {vehicle ? (
          <>
            <VehicleDetails
              make={vehicle.make}
              model={vehicle.model}
              year={vehicle.year}
              color={vehicle.color}
              licensePlate={vehicle.license_plate}
              vin={vehicle.vin}
              bodyType={vehicle.body_type}
              startingMiles={vehicle.current_meter_value}
            />

            <Section
              childCmp={
                <>
                  <Label text={`Ending ${countrySpecificDistanceUnit(country)}`} requiredFieldInd={true} />
                  <NumericInput
                    style={textBoxStyle}
                    name="endingOdometer"
                    dataTestId="endingOdometer"
                    value={currentVehicleState.endingOdometer}
                    min={currentVehicleState.endingOdometer}
                    step="any"
                    required={true}
                    onChange={onEndingOdometerChange}
                  />
                  <p>Please double check the odometer value in the vehicle as part of the Exchange process.</p>
                </>
              }
            />
          </>
        ) : (
          <LoadingSpinner />
        )}


        {statuses ? (
          <Section
            childCmp={
              <>
                <Label text="Fleetio status for returning vehicle" requiredFieldInd={true} />
                <DropDownMenu
                  menuName="fleetioStatus"
                  required={true}
                  defaultVal={{ value: currentVehicleState.fleetioStatusId, label: currentVehicleState.fleetioStatusName }}
                  choices={statuses.map((item: IFleetioStatus) => ({
                    label: item.name,
                    value: item.id,
                    enabled: true,
                  }))}
                  onSelect={onSelectStatus}
                  testId="fleetioStatus"
                />
              </>
            }
          />
        ) : (
          <LoadingSpinner />
        )}

        <SubmitBtn text="Next" breadcrumbObject={breadcrumbObject} disabled={isSubmitDisabled} />
      </form>
    </>
  );
}

const Section = ({ childCmp }: { childCmp: JSX.Element }) => (
  <div style={{ margin: '2.5vh 0vw' }}>{childCmp}</div>
);

const LoadingSpinner = () => (
  <div style={{ height: '5vh' }}>
    <Elliptical />
  </div>
);
